import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { VpnKey } from '@mui/icons-material';
import { dataverseService } from '../services/dataverseService';
import { listKeysByMobile, revokeAllKeysByMobile, revokeKeyById } from '../services/unlocKeyService';
import { getDataverseErrorMessage } from '../lib/dataverseErrorHandler';
import { formatKeyTime, mapUnlocKeys } from '../lib/phoneKeys';

export default function PhoneKeysDialog({ open, phone, onClose }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [keys, setKeys] = useState([]);
  const [selected, setSelected] = useState(() => new Set());

  const loadKeys = async () => {
    if (!phone) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const [listed, locks] = await Promise.all([
        listKeysByMobile(phone.mobileNumber),
        dataverseService.lockService.getAll()
      ]);

      if (!listed) {
        return;
      }

      setKeys(mapUnlocKeys(listed, locks));
      setSelected(new Set());
    } catch (loadError) {
      setKeys([]);
      setError(getDataverseErrorMessage(loadError, 'Kunne ikke hente nøkler for telefonen.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !phone) {
      return;
    }

    loadKeys();
  }, [open, phone?.phoneId]);

  const toggle = (keyId) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(keyId)) {
        next.delete(keyId);
      } else {
        next.add(keyId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((current) =>
      current.size === keys.length ? new Set() : new Set(keys.map((key) => key.id))
    );
  };

  const runRevokes = async (keyIds, all) => {
    if (!phone) {
      return;
    }

    const confirmed = window.confirm(
      all
        ? `Trekker tilbake alle Unloc-nøkler for ${phone.mobileNumber}. Fortsette?`
        : `Trekker tilbake ${keyIds.length} valgte nøkkel(er) for ${phone.mobileNumber}. Fortsette?`
    );
    if (!confirmed) {
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (all) {
        const result = await revokeAllKeysByMobile(phone.mobileNumber);
        if (!result) {
          return;
        }
      } else {
        for (const keyId of keyIds) {
          const key = keys.find((entry) => entry.id === keyId);
          const result = await revokeKeyById(keyId, {
            mobileNumber: phone.mobileNumber,
            lockId: key?.lockId
          });
          if (!result) {
            return;
          }
        }
      }

      setSuccessMessage(all ? 'Alle nøkler er trukket tilbake.' : 'Valgte nøkler er trukket tilbake.');
      await loadKeys();
    } catch (revokeError) {
      setError(getDataverseErrorMessage(revokeError, 'Kunne ikke trekke tilbake nøkler.'));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = selected.size;
  const allSelected = keys.length > 0 && selectedCount === keys.length;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VpnKey color="primary" />
        Nøkler for {phone?.name || 'telefon'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {phone?.mobileNumber} — gyldige nøkler hentet live fra Unloc. Lagres ikke i OpenCare.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : keys.length === 0 ? (
          <Alert severity="info">Ingen gyldige nøkler funnet for denne telefonen.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedCount > 0 && !allSelected}
                    onChange={toggleAll}
                    disabled={submitting}
                  />
                </TableCell>
                <TableCell>Lås</TableCell>
                <TableCell>Utstedt</TableCell>
                <TableCell>Gyldig til</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key.id || key.lockId} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(key.id)}
                      onChange={() => toggle(key.id)}
                      disabled={submitting}
                    />
                  </TableCell>
                  <TableCell>{key.name}</TableCell>
                  <TableCell>{formatKeyTime(key.grantedOn)}</TableCell>
                  <TableCell>{key.expiresOn ? formatKeyTime(key.expiresOn) : 'Varig'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={submitting}>
          Lukk
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          color="warning"
          variant="outlined"
          disabled={submitting || selectedCount === 0}
          onClick={() => runRevokes([...selected], false)}
        >
          {submitting ? 'Behandler…' : `Trekk tilbake valgte (${selectedCount})`}
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={submitting || keys.length === 0}
          onClick={() => runRevokes([], true)}
        >
          Trekk tilbake alle
        </Button>
      </DialogActions>
    </Dialog>
  );
}
